import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, Phone, User, MapPin, Calendar, ExternalLink, Download, Share2, Pencil, Plus, Minus, Trash2, RefreshCw, Copy, Link2, CreditCard, Send, Eye, EyeOff, FileSpreadsheet } from 'lucide-react';
import { downloadInvoice, shareInvoiceViaWhatsApp } from '@/utils/invoiceGenerator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminOrdersList = ({ adminToken }) => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const LIMIT = 10;

  const [searchPhone, setSearchPhone] = useState('');
  const [searchName, setSearchName] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCancelled, setShowCancelled] = useState(false);
  
  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [editDeliveryCharge, setEditDeliveryCharge] = useState(0);
  const [saving, setSaving] = useState(false);
  const [newPaymentLink, setNewPaymentLink] = useState(null);
  // Customer detail editing
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [phoneError, setPhoneError] = useState('');
  
  // Payment link dialog state
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);

  useEffect(() => {
    loadFresh();
    fetchProducts();
  }, [showCancelled]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?include_out_of_stock=true`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const buildParams = (phone, name, status, skpAmt) => {
    const params = new URLSearchParams();
    if (phone) params.append('phone', phone);
    if (name) params.append('name', name);
    if (status && status !== 'all') params.append('status', status);
    if (showCancelled) params.append('include_cancelled', 'true');
    params.append('limit', LIMIT);
    params.append('skip', skpAmt);
    return params;
  };

  // Fresh load — resets list
  const loadFresh = async (phone = '', name = '', status = '') => {
    setLoading(true);
    setSkip(0);
    try {
      const params = buildParams(phone, name, status, 0);
      const response = await axios.get(`${API}/admin/orders?${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setOrders(response.data.orders);
      setTotal(response.data.total);
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Show More — appends to list
  const loadMore = async () => {
    setLoadingMore(true);
    const nextSkip = skip + LIMIT;
    try {
      const params = buildParams(searchPhone, searchName, statusFilter, nextSkip);
      const response = await axios.get(`${API}/admin/orders?${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setOrders(prev => [...prev, ...response.data.orders]);
      setTotal(response.data.total);
      setHasMore(response.data.has_more);
      setSkip(nextSkip);
    } catch (error) {
      console.error('Failed to load more orders:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSearch = () => {
    loadFresh(searchPhone, searchName, statusFilter);
  };

  // Re-fetch current page after status change (keep position)
  const refreshCurrent = () => loadFresh(searchPhone, searchName, statusFilter);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/orders/${orderId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      // Optimistic update to avoid full reload
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, order_status: newStatus } : o));
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  // Payment link functions
  const handleGeneratePaymentLink = (order) => {
    setPaymentOrder(order);
    setGeneratedLink(null);
    setShowPaymentDialog(true);
  };

  const generatePaymentLink = async (sendSms = true) => {
    if (!paymentOrder) return;
    
    setGeneratingLink(true);
    try {
      const response = await axios.post(
        `${API}/admin/orders/${paymentOrder.id}/generate-payment-link?send_sms=${sendSms}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      setGeneratedLink(response.data.payment_link);
      toast.success(response.data.message);
      setOrders(prev => prev.map(o => o.id === paymentOrder.id ? { ...o, payment_link: response.data.payment_link } : o));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate payment link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleEditClick = (order) => {
    setEditingOrder(order);
    setEditItems(order.items?.map(item => ({
      product_id: item.product_id,
      quantity: item.quantity,
      product: item.product,
      name: item.product?.name || item.name,
      price: item.product?.price || item.price
    })) || []);
    setEditDeliveryCharge(order.delivery_charge || 0);
    setNewPaymentLink(null);
    // Pre-fill customer details
    setEditName(order.name || '');
    setEditPhone(order.phone || '');
    setEditAddress(order.delivery_address || '');
    setPhoneError('');
    setShowEditDialog(true);
  };

  const handleAddProduct = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    const existing = editItems.find(i => i.product_id === productId);
    if (existing) {
      setEditItems(editItems.map(i =>
        i.product_id === productId ? { ...i, quantity: i.quantity + 1 } : i
      ));
    } else {
      setEditItems([...editItems, {
        product_id: productId,
        product: product,
        quantity: 1,
        name: product.name,
        price: product.price
      }]);
    }
  };

  const handleUpdateQuantity = (productId, delta) => {
    setEditItems(editItems.map(i => {
      if (i.product_id === productId) {
        const newQty = i.quantity + delta;
        return newQty > 0 ? { ...i, quantity: newQty } : i;
      }
      return i;
    }).filter(i => i.quantity > 0));
  };

  const handleRemoveItem = (productId) => {
    setEditItems(editItems.filter(i => i.product_id !== productId));
  };

  const calculateEditSubtotal = () => {
    return editItems.reduce((sum, i) => sum + ((i.price || i.product?.price || 0) * i.quantity), 0);
  };

  const calculateEditTotal = () => {
    return calculateEditSubtotal() + Number(editDeliveryCharge || 0);
  };

  const validatePhone = (phone) => {
    const cleaned = phone.replace(/\s/g, '').replace(/-/g, '');
    let digits = cleaned;
    if (digits.startsWith('+91')) digits = digits.slice(3);
    else if (digits.startsWith('91') && digits.length === 12) digits = digits.slice(2);
    if (!/^\d{10}$/.test(digits)) return 'Phone must be 10 digits (e.g. 9876543210 or +91-9876543210)';
    return '';
  };

  const handlePhoneChange = (val) => {
    setEditPhone(val);
    setPhoneError(validatePhone(val));
  };

  const handleSaveOrder = async () => {
    if (editItems.length === 0) {
      toast.error('Order must have at least one item');
      return;
    }

    // Validate phone if changed
    if (editPhone && editPhone !== editingOrder.phone) {
      const err = validatePhone(editPhone);
      if (err) { setPhoneError(err); toast.error(err); return; }
    }

    setSaving(true);
    try {
      // Save items & delivery charge
      const response = await axios.put(
        `${API}/admin/orders/${editingOrder.id}/items`,
        {
          items: editItems.map(i => ({
            product_id: i.product_id,
            quantity: i.quantity,
            name: i.name || i.product?.name,
            price: i.price || i.product?.price,
            description: i.product?.description
          })),
          delivery_charge: Number(editDeliveryCharge)
        },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );

      // Save customer details if any changed
      const nameChanged = editName.trim() && editName.trim() !== editingOrder.name;
      const phoneChanged = editPhone.trim() && editPhone.trim() !== editingOrder.phone;
      const addressChanged = editAddress.trim() && editAddress.trim() !== editingOrder.delivery_address;
      if (nameChanged || phoneChanged || addressChanged) {
        await axios.patch(
          `${API}/admin/orders/${editingOrder.id}/customer`,
          { name: editName, phone: editPhone, delivery_address: editAddress },
          { headers: { Authorization: `Bearer ${adminToken}` } }
        );
      }

      const updatedOrder = {
        ...response.data.order,
        ...(nameChanged ? { name: editName.trim() } : {}),
        ...(phoneChanged ? { phone: editPhone.trim() } : {}),
        ...(addressChanged ? { delivery_address: editAddress.trim() } : {})
      };

      if (response.data.new_payment_link) {
        setNewPaymentLink(response.data.new_payment_link);
        toast.success('Order updated! New payment link generated.');
      } else {
        toast.success('Order updated successfully!');
        setShowEditDialog(false);
      }
      setOrders(prev => prev.map(o => o.id === editingOrder.id ? { ...o, ...updatedOrder } : o));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      pending_payment: 'bg-orange-100 text-orange-800',
      confirmed: 'bg-blue-100 text-blue-800',
      preparing: 'bg-purple-100 text-purple-800',
      ready: 'bg-cyan-100 text-cyan-800',
      out_for_delivery: 'bg-indigo-100 text-indigo-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Group products by category for the edit dialog
  const productsByCategory = products.reduce((acc, product) => {
    const cat = product.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(product);
    return acc;
  }, {});

  const handleExportCustomers = async () => {
    try {
      toast.info('Downloading customer data...');
      const response = await axios.get(`${API}/admin/customers/export`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `customers_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Customer data downloaded!');
    } catch (error) {
      toast.error('Failed to export customers');
    }
  };

  return (
    <Card className="p-3 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-accent">All Orders</h2>
          {total > 0 && <p className="text-xs text-muted-foreground">{orders.length} of {total} orders</p>}
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleExportCustomers}
          className="gap-1.5 text-xs"
        >
          <FileSpreadsheet className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Export</span> CSV
        </Button>
      </div>
      
      {/* Filters */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        <div className="relative">
          <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Phone..."
            value={searchPhone}
            onChange={(e) => setSearchPhone(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-8 h-9 text-sm"
          />
        </div>
        <div className="relative">
          <User className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Name..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-8 h-9 text-sm"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="preparing">Preparing</SelectItem>
            <SelectItem value="ready">Ready</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={handleSearch} className="gap-1.5 h-9 text-sm">
          <Search className="h-3.5 w-3.5" />
          Search
        </Button>
        <Button 
          variant={showCancelled ? "default" : "outline"}
          onClick={() => setShowCancelled(!showCancelled)}
          className={`gap-1.5 h-9 text-sm col-span-2 sm:col-span-1 ${showCancelled ? 'bg-red-600 hover:bg-red-700' : ''}`}
        >
          {showCancelled ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          {showCancelled ? 'Hide' : 'Show'} Cancelled
        </Button>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin mx-auto text-primary mb-2" />
          <p className="text-sm text-muted-foreground">Loading orders...</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">No orders found</div>
      ) : (
        <div className="space-y-2.5">
          {orders.map((order) => (
            <Card key={order.id} className={`p-3 border-l-4 ${order.is_subscription_order ? 'border-l-emerald-500' : 'border-l-primary'}`}>
              {/* Top row: ID + status + total */}
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <span className="font-bold text-accent text-sm">#{order.id.slice(0, 8).toUpperCase()}</span>
                  <Badge className={`${getStatusColor(order.order_status)} text-xs`}>
                    {order.order_status}
                  </Badge>
                  {order.is_subscription_order && (
                    <Badge className="bg-emerald-100 text-emerald-800 text-xs">Sub</Badge>
                  )}
                  {order.payment_link && (
                    <a href={order.payment_link} target="_blank" rel="noopener noreferrer">
                      <Badge variant="outline" className="gap-1 text-xs cursor-pointer hover:bg-primary/10">
                        <ExternalLink className="h-2.5 w-2.5" />
                        Pay Link
                      </Badge>
                    </a>
                  )}
                </div>
                <span className="text-base font-bold text-primary shrink-0">₹{order.total}</span>
              </div>

              {/* Customer info — compact grid */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-muted-foreground mb-2">
                <span className="flex items-center gap-1 truncate">
                  <User className="h-3 w-3 shrink-0" />{order.name}
                </span>
                <span className="flex items-center gap-1 truncate">
                  <Phone className="h-3 w-3 shrink-0" />{order.phone}
                </span>
                <span className="flex items-center gap-1 truncate col-span-2">
                  <MapPin className="h-3 w-3 shrink-0" />
                  {order.delivery_type === 'pickup' ? 'Store Pickup' : (order.delivery_address || 'N/A')}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3 shrink-0" />
                  {order.scheduled_date || new Date(order.created_at).toLocaleDateString('en-IN')}
                </span>
              </div>

              {/* Items — truncated */}
              <p className="text-xs text-muted-foreground mb-3 truncate">
                {order.items?.map(i => `${i.product?.name || i.name} ×${i.quantity}`).join(' · ')}
              </p>

              {/* Actions row */}
              <div className="flex items-center gap-2 flex-wrap">
                <Select
                  value={order.order_status}
                  onValueChange={(value) => handleStatusChange(order.id, value)}
                >
                  <SelectTrigger className="h-8 w-36 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="preparing">Preparing</SelectItem>
                    <SelectItem value="ready">Ready</SelectItem>
                    <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" className="h-8 px-2.5 gap-1 text-xs" onClick={() => handleEditClick(order)}>
                  <Pencil className="h-3 w-3" />Edit
                </Button>
                <Button size="sm" variant="outline" className="h-8 px-2.5" onClick={() => { try { downloadInvoice(order); toast.success('Downloaded!'); } catch { toast.error('Failed'); } }}>
                  <Download className="h-3 w-3" />
                </Button>
                <Button size="sm" variant="outline" className="h-8 px-2.5" onClick={() => { try { shareInvoiceViaWhatsApp(order); toast.success('Opening WhatsApp...'); } catch { toast.error('Failed'); } }}>
                  <Share2 className="h-3 w-3" />
                </Button>
              </div>
            </Card>
          ))}

          {/* Show More / Load More */}
          {hasMore && (
            <div className="pt-2">
              <Button
                variant="outline"
                className="w-full h-10 gap-2 text-sm"
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="show-more-orders"
              >
                {loadingMore
                  ? <><RefreshCw className="h-4 w-4 animate-spin" /> Loading...</>
                  : <>Show More Orders ({total - orders.length} remaining)</>
                }
              </Button>
            </div>
          )}
          {!hasMore && orders.length > 0 && total > LIMIT && (
            <p className="text-center text-xs text-muted-foreground py-2">All {total} orders loaded</p>
          )}
        </div>
      )}

      {/* Edit Order Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-primary" />
              Edit Order #{editingOrder?.id?.slice(0, 8).toUpperCase()}
            </DialogTitle>
          </DialogHeader>

          {newPaymentLink ? (
            <div className="py-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200 mb-4">
                <h4 className="font-bold text-green-800 mb-2">Order Updated!</h4>
                <p className="text-sm text-green-700 mb-3">New total: ₹{calculateEditTotal()}</p>
                <p className="text-sm text-green-700 mb-3">A new payment link has been generated:</p>
                <div className="flex items-center gap-2">
                  <Input value={newPaymentLink} readOnly className="flex-1 text-sm" />
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(newPaymentLink);
                      toast.success('Payment link copied!');
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <a href={newPaymentLink} target="_blank" rel="noopener noreferrer">
                    <Button size="icon" variant="outline">
                      <Link2 className="h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </div>
              <Button className="w-full" onClick={() => setShowEditDialog(false)}>
                Close
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Customer Details Section */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <Label className="text-sm font-semibold mb-3 block flex items-center gap-2">
                  <User className="h-4 w-4" /> Customer Details
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Name</Label>
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder="Customer name"
                      className="mt-1 h-8 text-sm"
                      data-testid="edit-customer-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Phone</Label>
                    <Input
                      value={editPhone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      placeholder="10-digit number"
                      className={`mt-1 h-8 text-sm ${phoneError ? 'border-red-500' : ''}`}
                      data-testid="edit-customer-phone"
                    />
                    {phoneError && <p className="text-xs text-red-500 mt-1">{phoneError}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-muted-foreground">Delivery Address</Label>
                    <Input
                      value={editAddress}
                      onChange={(e) => setEditAddress(e.target.value)}
                      placeholder="Delivery address"
                      className="mt-1 h-8 text-sm"
                      data-testid="edit-customer-address"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Add Products */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Add Products</Label>
                <div className="border rounded-lg p-3 max-h-[300px] overflow-y-auto space-y-3">
                  {Object.entries(productsByCategory).map(([category, catProducts]) => (
                    <div key={category}>
                      <p className="text-xs font-medium text-muted-foreground uppercase mb-1">{category}</p>
                      <div className="space-y-1">
                        {catProducts.map((product) => (
                          <div
                            key={product.id}
                            className="flex items-center justify-between p-2 rounded hover:bg-gray-50 cursor-pointer text-sm"
                            onClick={() => handleAddProduct(product.id)}
                          >
                            <span className="truncate flex-1">{product.name}</span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="font-medium text-primary">₹{product.price}</span>
                              <Button size="icon" variant="ghost" className="h-6 w-6">
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Current Items */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Order Items</Label>
                <div className="border rounded-lg p-3 space-y-2">
                  {editItems.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No items in order</p>
                  ) : (
                    editItems.map((item) => (
                      <div key={item.product_id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm truncate flex-1">{item.name || item.product?.name}</span>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-6 w-6"
                            onClick={() => handleUpdateQuantity(item.product_id, -1)}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-6 w-6"
                            onClick={() => handleUpdateQuantity(item.product_id, 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-red-500"
                            onClick={() => handleRemoveItem(item.product_id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                          <span className="w-14 text-right text-sm font-medium">
                            ₹{(item.price || item.product?.price || 0) * item.quantity}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                  
                  {/* Totals */}
                  <div className="pt-3 border-t space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span>₹{calculateEditSubtotal()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Delivery Charge:</Label>
                      <Input
                        type="number"
                        value={editDeliveryCharge}
                        onChange={(e) => setEditDeliveryCharge(e.target.value)}
                        className="w-24 h-8 text-right"
                        min="0"
                      />
                    </div>
                    <div className="flex justify-between font-bold text-lg pt-2 border-t">
                      <span>Total:</span>
                      <span className="text-primary">₹{calculateEditTotal()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          )}

          {!newPaymentLink && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveOrder} disabled={saving} className="gap-2">
                {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminOrdersList;
